import { supabase } from "@/integrations/supabase/client";

export interface BetaSignupStatus {
  enabled: boolean;
  baseline: number;
  extra: number;
  ceiling: number;
  taken: number;
  remaining: number;
  open: boolean;
}

export type BetaWaitlistStatus = "waiting" | "invited" | "registered";

export interface BetaWaitlistEntry {
  id: string;
  full_name: string;
  email: string;
  status: BetaWaitlistStatus;
  created_at: string;
  invited_at: string | null;
}

export const BETA_STATUS_LABELS: Record<BetaWaitlistStatus, string> = {
  waiting: "En attente",
  invited: "Invitée",
  registered: "Inscrite",
};

export const BETA_FULL_MESSAGE = "Les inscriptions de la bêta sont complètes.";

/** État du palier bêta (lisible sans connexion). */
export async function fetchBetaSignupStatus(): Promise<BetaSignupStatus> {
  const { data, error } = await supabase.rpc("beta_signup_status");
  if (error) throw error;
  return data as unknown as BetaSignupStatus;
}

/** Enregistre le palier : le plafond = comptes actuels + nouveaux inscrits. */
export async function saveBetaSignupTier(enabled: boolean, extra: number) {
  const { error } = await supabase.rpc("set_beta_signup_tier", { p_enabled: enabled, p_extra: extra });
  if (error) throw error;
}

/** Ajoute une demande ; renvoie le message serveur français en cas de refus. */
export async function joinBetaWaitlist(fullName: string, email: string): Promise<string | null> {
  const { error } = await supabase.rpc("join_beta_waitlist", { p_full_name: fullName, p_email: email });
  if (!error) return null;
  // Les refus métier (P0001) portent déjà un message français destiné au membre.
  if (error.code === "P0001" && error.message) return error.message;
  console.error("Liste d'attente :", error);
  return "Impossible d'enregistrer ta demande. Réessaie plus tard.";
}

export async function fetchBetaWaitlist(): Promise<BetaWaitlistEntry[]> {
  const { data, error } = await supabase
    .from("beta_waitlist")
    .select("id, full_name, email, status, created_at, invited_at")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as BetaWaitlistEntry[];
}

/** Envoie les invitations ; renvoie le nombre d'e-mails partis. */
export async function inviteBetaWaitlist(payload: { ids?: string[]; allWaiting?: boolean }): Promise<number> {
  const { data, error } = await supabase.functions.invoke("invite-beta-waitlist", { body: payload });
  if (error) throw error;
  return Number((data as { sent?: number })?.sent ?? 0);
}
